import { ReactElement } from 'react';

import {
   CurrentPractitionerQuery,
   PractitionerDetails,
   PractitionerView,
   PractitionerViewQuery,
} from '@dreryk/egabinet-personnel-frontend-api';

import { useCqrsGet, useCqrsSearch } from '@dreryk/command-query-react-common';
import { Direction, paginations } from '@dreryk/command-query-ws-client';
import { LoaderSpinner, Message, MessageType } from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { protectFeature } from '~libs.shared.system';

import { translationKeys } from '../translations';

type PractitionerFieldLoaderChildrenRenderPropsWithPractitioners = {
   practitioners: PractitionerView[];
   currentPractitioner?: never;
};
type PractitionerFieldLoaderChildrenRenderPropsWithCurrentDoctor = {
   practitioners?: never;
   currentPractitioner: PractitionerDetails;
};

type PractitionerFieldLoaderProps = {
   children: (
      renderProps:
         | PractitionerFieldLoaderChildrenRenderPropsWithPractitioners
         | PractitionerFieldLoaderChildrenRenderPropsWithCurrentDoctor
   ) => ReactElement;
};

export const WaitlistFormLoader = (props: PractitionerFieldLoaderProps): ReactElement => {
   const canSearchPractitioners = protectFeature('searchingAllPractitioners', true, false);
   const { data: practitioners, loading: practitionersLoading, error: practitionersFetchError } = useCqrsSearch(
      () => {
         const query = new PractitionerViewQuery();
         query.jobPositions = Slot.of(['DOCTOR']);
         return query;
      },
      paginations.all<PractitionerView>().orderedBy('lastName', Direction.ASCENDING),
      { enabled: canSearchPractitioners }
   );

   const {
      data: currentPractitioner,
      loading: currentPractitionerLoading,
      error: currentPractitionerFetchError,
   } = useCqrsGet<PractitionerDetails>(() => new CurrentPractitionerQuery(), { enabled: !canSearchPractitioners });

   if (practitionersLoading || currentPractitionerLoading) {
      return <LoaderSpinner />;
   }

   if (practitionersFetchError || currentPractitionerFetchError) {
      return (
         <Message
            testId={'failedToFetchData'}
            label={translationKeys.fetchError}
            type={MessageType.NEGATIVE}
            displayIndicator
         />
      );
   }

   if (canSearchPractitioners) {
      return props.children({ practitioners: practitioners ?? [] });
   }
   return props.children({ currentPractitioner: currentPractitioner });
};
